import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import RegistrationsService from "services/RegistrationsService";

// Async Thunks
export const getRegistrations = createAsyncThunk(
  "registrations/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.getRegistrations();
      return response.data.registrations;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur");
    }
  }
);

export const getPendingRegistrations = createAsyncThunk(
  "registrations/getPending",
  async (_, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.getPendingRegistrations();
      return response.data.groupedRegistrations;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur");
    }
  }
);

export const getRegistrationsByType = createAsyncThunk(
  "registrations/getByType",
  async (type, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.getRegistrationsByType(type);
      return response.data.registrations;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur");
    }
  }
);

export const getRegistrationById = createAsyncThunk(
  "registrations/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.getRegistrationById(id);
      return response.data.registration;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur");
    }
  }
);

export const validateRegistration = createAsyncThunk(
  "registrations/validate",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.validateRegistration(
        id,
        data
      );
      return response.data.registration;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || "Erreur"
      );
    }
  }
);

export const rejectRegistration = createAsyncThunk(
  "registrations/reject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.rejectRegistration(id, {
        reason,
      });
      return response.data.registration;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || "Erreur"
      );
    }
  }
);

export const updateReceipt = createAsyncThunk(
  "registrations/updateReceipt",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.updateReceipt(
        id,
        data
      );
      return response.data.registration;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur");
    }
  }
);

export const createTutorialRegistration = createAsyncThunk(
  "registrations/createTutorialRegistration",
  async ({ tutorialId, data }, { rejectWithValue }) => {
    try {
      const response = await RegistrationsService.createTutorialRegistration(
        tutorialId,
        data
      );
      return response.data.registration;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur");
    }
  }
);
const initialState = {
  registrations: [],
  pendingRegistrations: {},
  registration: null,
  loading: false,
  error: null,
};

const registrationsSlice = createSlice({
  name: "registrations",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRegistration: (state) => {
      state.registration = null;
    },
  },
  extraReducers: {
    // getRegistrations
    [getRegistrations.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getRegistrations.fulfilled]: (state, { payload }) => {
      state.registrations = payload;
      state.loading = false;
      state.error = null;
    },
    [getRegistrations.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },

    // getPendingRegistrations
    [getPendingRegistrations.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getPendingRegistrations.fulfilled]: (state, { payload }) => {
      state.pendingRegistrations = payload;
      state.loading = false;
      state.error = null;
    },
    [getPendingRegistrations.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },

    // getRegistrationsByType
    [getRegistrationsByType.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getRegistrationsByType.fulfilled]: (state, { payload }) => {
      state.registrations = payload;
      state.loading = false;
      state.error = null;
    },
    [getRegistrationsByType.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },

    // getRegistrationById
    [getRegistrationById.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getRegistrationById.fulfilled]: (state, { payload }) => {
      state.registration = payload;
      state.loading = false;
    },
    [getRegistrationById.rejected]: (state, { payload }) => {
      state.loading = false;
      state.error = payload;
      state.registration = null;
    },

    // validateRegistration
    [validateRegistration.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [validateRegistration.fulfilled]: (state, { payload }) => {
      state.registration = payload;
      state.registrations = state.registrations.map((reg) =>
        reg._id === payload._id ? payload : reg
      );
      state.loading = false;
    },
    [validateRegistration.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },

    // rejectRegistration
    [rejectRegistration.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [rejectRegistration.fulfilled]: (state, { payload }) => {
      state.registration = payload;
      state.registrations = state.registrations.map((reg) =>
        reg._id === payload._id ? payload : reg
      );
      state.loading = false;
    },
    [rejectRegistration.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },

    // createTutorialRegistration
    [createTutorialRegistration.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [createTutorialRegistration.fulfilled]: (state, { payload }) => {
      state.registration = payload;
      state.loading = false;
    },
    [createTutorialRegistration.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },
  },
});

export const { clearError, clearRegistration } = registrationsSlice.actions;

export default registrationsSlice.reducer;
